/**
 * ============================================================
 * System       : CSAF Alert Finance
 * Objective    : Automated Reserve Fund Notification System
 * File         : frontend/src/pages/History.jsx
 * Description  : Action history log — table view
 * ------------------------------------------------------------
 * Developer    : Chokchai Thainkhao (Head of IT)
 * Co-Author    : AI Assisted (Claude) — โครงสร้างและฟังก์ชันหลัก
 * Modified &
 *  Integrated  : Chokchai Thainkhao
 * Version      : 1.0.0
 * Date         : May 2026
 * Copyright    : © 2026 Chokchai T. All rights reserved.
 * ============================================================
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { getHistory, clearHistory } from '../api';
import { useToast } from '../App';
import Paginator, { usePagination } from '../components/Paginator';

const TYPE_META = {
  login:   { label: 'เข้าสู่ระบบ',  bg: 'bg-blue-100',   text: 'text-blue-700'   },
  sent:    { label: 'ส่ง Alert',     bg: 'bg-indigo-100', text: 'text-indigo-700' },
  auto:    { label: 'อัตโนมัติ',    bg: 'bg-purple-100', text: 'text-purple-700' },
  cleared: { label: 'เคลียร์',       bg: 'bg-green-100',  text: 'text-green-700'  },
  import:  { label: 'นำเข้า/แก้ไข', bg: 'bg-gray-100',   text: 'text-gray-600'   },
  user:    { label: 'จัดการผู้ใช้งาน', bg: 'bg-amber-100', text: 'text-amber-700' },
};

const DAYS_OPTIONS = [
  { value: '7',   label: '7 วันล่าสุด' },
  { value: '30',  label: '30 วันล่าสุด' },
  { value: '90',  label: '90 วันล่าสุด' },
  { value: 'all', label: 'ทั้งหมด' },
];

const TYPE_OPTIONS = [
  { value: 'all',     label: 'ทุกประเภท' },
  { value: 'login',   label: 'เข้าสู่ระบบ' },
  { value: 'sent',    label: 'ส่ง Alert' },
  { value: 'auto',    label: 'อัตโนมัติ' },
  { value: 'cleared', label: 'เคลียร์' },
  { value: 'import',  label: 'นำเข้า/แก้ไข' },
  { value: 'user',    label: 'จัดการผู้ใช้งาน' },
];

const THAI_MONTHS_SHORT = ['','ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.'];

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr.replace(' ', 'T'));
  const day  = d.getDate();
  const mon  = THAI_MONTHS_SHORT[d.getMonth() + 1];
  const year = d.getFullYear() + 543;
  const hh   = String(d.getHours()).padStart(2, '0');
  const mm   = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${('0' + (d.getMonth() + 1)).slice(-2)}/${year}  ${hh}:${mm}`;
}

export default function History({ currentUser }) {
  const [history, setHistory]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [daysFilter, setDaysFilter] = useState('30');
  const [search, setSearch]     = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage]         = useState(1);
  const [confirmClear, setConfirmClear] = useState(false);
  const { addToast } = useToast();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getHistory(typeFilter, daysFilter);
      setHistory(res.data);
    } catch (e) {
      addToast('error', e.response?.data?.detail || 'โหลดประวัติไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [addToast, typeFilter, daysFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const isAdmin = currentUser?.role === 'admin';

  const filtered = useMemo(() => {
    if (!search.trim()) return history;
    const q = search.trim().toLowerCase();
    return history.filter(h =>
      (h.detail || '').toLowerCase().includes(q) ||
      (h.user_email || '').toLowerCase().includes(q) ||
      String(h.record_id || '').includes(q)
    );
  }, [history, search]);

  useEffect(() => { setPage(1); }, [typeFilter, daysFilter, search, pageSize]);
  const { pageItems, safePage } = usePagination(filtered, pageSize, page);

  const totalCount   = history.length;
  const alertCount   = history.filter(h => h.type === 'sent' || h.type === 'auto').length;
  const clearedCount = history.filter(h => h.type === 'cleared').length;
  const errorCount   = history.filter(h => h.status === 'error').length;

  const handleClearLog = async () => {
    try {
      await clearHistory();
      addToast('success', 'ล้าง Log สำเร็จ');
      setConfirmClear(false);
      setHistory([]);
    } catch (e) {
      addToast('error', e.response?.data?.detail || 'ล้าง Log ไม่สำเร็จ');
    }
  };

  const selectCls = 'border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-gray-800">ประวัติการใช้งาน</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={selectCls}>
            {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={daysFilter} onChange={(e) => setDaysFilter(e.target.value)} className={selectCls}>
            {DAYS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button
            onClick={loadData}
            className="px-4 py-2 border border-primary text-primary rounded-lg text-sm font-medium hover:bg-primary-light transition-colors">
            รีเฟรช
          </button>
          {isAdmin && (
            <button
              onClick={() => setConfirmClear(true)}
              className="px-4 py-2 bg-danger text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors">
              ล้าง Log
            </button>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'ทั้งหมด',    value: totalCount,   color: 'bg-white border border-gray-200', textColor: 'text-gray-800' },
          { label: 'แจ้งเตือน',  value: alertCount,   color: 'bg-white border border-gray-200', textColor: 'text-indigo-600' },
          { label: 'เคลียร์',    value: clearedCount, color: 'bg-white border border-gray-200', textColor: 'text-success' },
          { label: 'ข้อผิดพลาด', value: errorCount,   color: 'bg-white border border-gray-200', textColor: 'text-danger' },
        ].map((c) => (
          <div key={c.label} className={`rounded-xl p-4 ${c.color} shadow-sm`}>
            <div className={`text-2xl font-bold ${c.textColor}`}>{c.value}</div>
            <div className="text-sm text-gray-500 mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="ค้นหา รายละเอียด, ผู้ดำเนินการ..."
          className="w-full pl-8 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">×</button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="text-4xl mb-2">📂</div>
            <div className="text-sm">{search ? 'ไม่พบรายการที่ค้นหา' : 'ยังไม่มีประวัติ'}</div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left whitespace-nowrap">วันเวลา</th>
                    <th className="px-4 py-3 text-left">ประเภท</th>
                    <th className="px-4 py-3 text-left">รายละเอียด</th>
                    <th className="px-4 py-3 text-left whitespace-nowrap">ผู้ดำเนินการ</th>
                    <th className="px-4 py-3 text-left whitespace-nowrap">IP</th>
                    <th className="px-4 py-3 text-center">สถานะ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {pageItems.map((h) => {
                    const meta = TYPE_META[h.type] || TYPE_META.import;
                    const isError = h.status === 'error';
                    return (
                      <tr key={h.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                          {formatDateTime(h.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${meta.bg} ${meta.text}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700 max-w-xs">
                          <span className="line-clamp-2">{h.detail || '-'}</span>
                        </td>
                        <td className="px-4 py-3 text-primary text-xs">
                          {h.user_email || <span className="text-gray-400">system</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs font-mono whitespace-nowrap">
                          {h.ip_address || '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs font-medium ${isError ? 'text-danger' : 'text-success'}`}>
                            {isError ? 'ล้มเหลว' : 'สำเร็จ'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Paginator
              total={filtered.length} pageSize={pageSize} setPageSize={setPageSize}
              page={safePage} setPage={setPage}
            />
          </>
        )}
      </div>

      {/* Confirm clear modal */}
      {confirmClear && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-bold text-gray-800 mb-2">ยืนยันการล้าง Log</h3>
            <p className="text-sm text-gray-600 mb-5">ประวัติทั้งหมดจะถูกลบอย่างถาวร ไม่สามารถกู้คืนได้</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmClear(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                ยกเลิก
              </button>
              <button onClick={handleClearLog}
                className="px-4 py-2 bg-danger text-white rounded-lg text-sm font-medium hover:bg-red-700">
                ล้าง Log
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
